/**
 * Application shell. Renders the login form until the socket says the session
 * is open, and the console after that. There is no route guard beyond this:
 * without an authenticated socket there is no data to render at all.
 */
import { OtpPrompt } from './components/OtpPrompt.tsx';
import { Campaigns } from './routes/Campaigns.tsx';
import { useRef, useState } from 'react';
import { Layout, type NavItem } from './components/Layout.tsx';
import { Notice } from './components/Notice.tsx';
import { Login } from './auth/Login.tsx';
import { Overview } from './routes/Overview.tsx';
import { Topology } from './routes/Topology.tsx';
import { Names } from './routes/Names.tsx';
import { useTab } from './router.tsx';
import { Services } from './routes/Services.tsx';
import { Agents } from './routes/Agents.tsx';
import { Links } from './routes/Links.tsx';
import { Storage } from './routes/Storage.tsx';
import { Minio } from './routes/Minio.tsx';
import { Devices } from './routes/Devices.tsx';
import { Restore } from './routes/Restore.tsx';
import { Dns } from './routes/Dns.tsx';
import { Mcp } from './routes/Mcp.tsx';
import { Events } from './routes/Events.tsx';
import { Settings } from './routes/Settings.tsx';
import { useConnection } from './transport/hooks.ts';

// Список вкладок и их пути живут в роутере: там же, где проверяется, что путь
// есть у каждой. Держать перечисление в двух местах — значит однажды завести
// вкладку без адреса, что с восстановлением и помощником уже случилось.
import type { Tab } from './router.tsx';

/**
 * Разделы по смыслу, а не по алфавиту.
 *
 * Сверху то, ради чего консоль открывают: что защищено и куда всё стекается.
 * Ниже — чем это делается: агенты, связи, хранилища. В самом низу то, что
 * трогают редко. Порядок отражает частоту вопросов владельца, а не устройство
 * приложения.
 */
// `id: Tab`, а не просто строка: пункт меню с опечаткой в идентификаторе
// компилировался бы молча и просто не открывался бы по нажатию.
const NAV: (NavItem & { id: Tab })[] = [
  { id: 'overview', label: 'Общая картина', glyph: '▦' },
  { id: 'services', label: 'Приложения',    glyph: '◈' },
  // «BackupHUB» звучит как название всей консоли — оно и стоит в заголовке.
  // Пункт меню, названный так же, теряется: владелец искал топологию и не
  // находил, хотя открывал её каждый раз мимо.
  { id: 'topology', label: 'Топология',     glyph: '◎' },

  { id: 'agents',   label: 'Агенты',        glyph: '⬡', group: 'Чем снимаем' },
  { id: 'minio',    label: 'Хранилище MinIO', glyph: '▣' },
  { id: 'links',    label: 'Связи',         glyph: '⇄' },
  { id: 'restore',  label: 'Восстановление', glyph: '⤓' },
  // Ночь переключения — свой экран: в час ночи важно не «где эта команда», а
  // «где мы сейчас», и порядок шагов сам по себе содержание.
  /*
   * Мероприятия — свой раздел, а не пункт среди наблюдения.
   *
   * Всё остальное в меню отвечает на вопрос «как оно сейчас». Мероприятие
   * отвечает на другой: «что мы делаем и что сделали». Через год именно этот
   * список объяснит, почему прод переехал в августе.
   */
  { id: 'campaigns', label: 'Мероприятия',  glyph: '⚑', group: 'Работы' },
  { id: 'dns',      label: 'Имена и домены', glyph: '⌘' },
  { id: 'storage',  label: 'Диски и сборщики', glyph: '▧' },

  { id: 'names',    label: 'Все названия',  glyph: '✎', group: 'Прочее' },
  { id: 'events',   label: 'События',       glyph: '☰' },
  { id: 'settings', label: 'Параметры',     glyph: '⚙' },
  { id: 'devices',  label: 'Ключи доступа', glyph: '⚿' },
  { id: 'mcp',      label: 'Помощник',      glyph: '✦' },
];

/**
 * Служебное сообщение о связи.
 *
 * Отдельной функцией, потому что состояний связи пять, а слов у них разное
 * количество: «подключаюсь» — это работа, «нет связи» — это работа с обратным
 * отсчётом, «ключ отклонён» — это конец, и ждать нечего.
 */
function connectionNotice(c: {
  status: string; reconnectInMs: number | null; attempt: number; message: string | null;
}): { tone: 'working' | 'warn' | 'bad'; title: string; detail?: string; footer?: string } | null {
  if (c.status === 'ready') return null;
  if (c.status === 'offline') {
    const secs = c.reconnectInMs != null ? Math.max(1, Math.round(c.reconnectInMs / 1000)) : null;
    return {
      tone: 'warn',
      title: 'Связь с сервером потеряна',
      // Главное, что нужно знать смотрящему: числа на экране не врут, они
      // просто остановились. Убирать их при обрыве значило бы прятать
      // последнее известное состояние в тот момент, когда оно ценнее всего.
      detail: 'Показанное на экране верно на момент обрыва и больше не обновляется.',
      ...(secs ? { footer: `Повтор через ${secs} с · попытка ${c.attempt}` } : {}),
    };
  }
  if (c.status === 'revoked') {
    return { tone: 'bad', title: 'Ключ отозван', detail: c.message ?? 'Войдите заново.' };
  }
  return {
    tone: 'working',
    title: c.status === 'authenticating' ? 'Проверяю ключ…' : 'Подключаюсь к серверу…',
  };
}

export function App({ route }: { route?: string } = {}): React.JSX.Element {
  const connection = useConnection();
  // Вкладка живёт в адресе, а не только в памяти: обновление страницы,
  // закладка и «назад» должны возвращать туда же, где владелец был.
  const [tab, setTab] = useTab('overview', route);

  if (
    connection.status === 'needs-credential' ||
    connection.status === 'signed-out' ||
    connection.status === 'locked' ||
    connection.status === 'auth-failed'
  ) {
    return <Login />;
  }

  /*
   * Экран не стирается на время обрыва.
   *
   * Раньше любое состояние, кроме `ready`, заменяло всю страницу одной серой
   * строкой: сеть моргнула — и владелец, читавший матрицу гипервизоров,
   * оказывался на пустом месте, откуда возвращаться надо вспоминая, где был.
   * Между тем ничего не пропало: показанное верно на момент обрыва.
   *
   * Поэтому после первого удачного подключения содержимое остаётся на месте,
   * а служебное сообщение приходит стеклянной панелью поверх. До первого
   * подключения показывать нечего — там панель и есть весь экран.
   */
  const everReady = useRef(false);
  if (connection.status === 'ready') everReady.current = true;
  const notice = connectionNotice(connection);

  if (!everReady.current) {
    return (
      <Layout>
        <main className="centered" />
        {notice && <Notice {...notice} />}
      </Layout>
    );
  }

  return (
    <>
      {notice && <Notice {...notice} />}
      {/* Окно кода — одно на всё приложение: сервер требует подтверждения на
          десяток разных действий, и просить его на каждом экране отдельно
          значило бы однажды пропустить экран. */}
      <OtpPrompt />
      <Layout nav={NAV} active={tab} onNavigate={(id) => setTab(id as Tab)}>
        {tab === 'overview' && <Overview />}
        {tab === 'services' && <Services />}
        {tab === 'topology' && <Topology />}
        {tab === 'agents' && <Agents />}
        {tab === 'links' && <Links />}
        {tab === 'storage' && <Storage />}
        {tab === 'minio' && <Minio />}
        {tab === 'names' && <Names />}
        {tab === 'restore' && <Restore />}
        {tab === 'campaigns' && <Campaigns />}
        {tab === 'dns' && <Dns />}
        {tab === 'events' && <Events />}
        {tab === 'settings' && <Settings />}
        {tab === 'devices' && <Devices />}
        {tab === 'mcp' && <Mcp />}
      </Layout>
    </>
  );
}
